/**
 * Authentication state for the whole SPA — the three backend modes.
 *
 *   none  → inert: a synthetic operator, no sign-in surface, today's behaviour
 *   local → email + password against the platform's own accounts; an access
 *           token in memory, a rotating refresh token in an HttpOnly cookie
 *   oidc  → the browser signs in at an external identity provider (Entra) and
 *           the SPA only ever holds the access token that comes back
 *
 * The mode is decided by the BACKEND (`GET /api/meta`), never by a build-time
 * flag: one image must serve dev, beta and prod, which is what makes
 * promotion a configuration change.
 *
 * The access token is held ONLY in memory (via lib/api's setAccessToken) so an
 * XSS payload cannot read it from storage. Both authenticated modes renew it
 * before expiry AND on demand when a request comes back 401 — browsers
 * throttle timers in background tabs, so the timer alone loses sessions.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  setAccessToken,
  setTokenRenewer,
  type AuthUser,
  type OidcConfig,
  ApiError,
} from "../lib/api";
import { AuthCtx, type AuthMode, type AuthState, type AuthStatus } from "./useAuth";
import i18n from "../i18n";

/** Access tokens live 15 min; refresh a minute early to avoid a 401 window. */
const REFRESH_LEAD_MS = 14 * 60 * 1000;

/** Synthetic principal for AUTH_MODE=none, so the UI can always name a user. */
const ANON_USER: AuthUser = {
  email: "operator",
  get full_name() {
    return i18n.t("auth.anonymousName");
  },
  role: "operator",
  must_change_password: false,
};

/** Roles ride inside the signed token and the BACKEND enforces them. This is
 *  presentation only — a browser that lies here gains nothing. */
function roleFromClaims(token: string): AuthUser["role"] {
  try {
    // JWTs are base64URL, not base64: '-' and '_' replace '+' and '/', and
    // the padding is dropped. atob() rejects both without this.
    const segment = token.split(".")[1] ?? "";
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = JSON.parse(atob(padded)) as { roles?: string[] | string };
    const raw = payload.roles ?? [];
    const roles = Array.isArray(raw) ? raw : String(raw).split(/[,\s]+/);
    if (roles.includes("sysadmin")) return "sysadmin";
    if (roles.includes("approver")) return "approver";
    return "operator";
  } catch {
    return "operator";
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<AuthMode>("none");
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const oidcRef = useRef<OidcConfig | null>(null);

  const enabled = mode !== "none";

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const signOutLocally = useCallback(() => {
    clearTimer();
    setAccessToken(null);
    setUser(null);
    setStatus("anonymous");
  }, []);

  // A ref lets the scheduled refresh call the latest applySession without the
  // callback depending on itself (which would recreate it every render).
  const applyRef = useRef<(token: string, u: AuthUser) => void>(() => undefined);
  const renewRef = useRef<() => Promise<string | null>>(async () => null);

  const applySession = useCallback((token: string, u: AuthUser) => {
    setAccessToken(token);
    setUser(u);
    setStatus("authenticated");
    setConfigError(null);
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      void renewRef.current();
    }, REFRESH_LEAD_MS);
  }, []);
  applyRef.current = applySession;

  /** One renewal path for both authenticated modes. Returns the new token,
   *  or null when the session is genuinely over. */
  const renew = useCallback(async (): Promise<string | null> => {
    const oidc = oidcRef.current;
    if (oidc) {
      const { acquireTokenSilently, currentAccount } = await import("./oidc");
      const token = await acquireTokenSilently(oidc);
      if (!token) {
        signOutLocally();
        return null;
      }
      const account = await currentAccount(oidc);
      applyRef.current(token, {
        email: account?.email ?? i18n.t("auth.unknownUser"),
        full_name: account?.name ?? account?.email ?? i18n.t("auth.unknownUser"),
        role: roleFromClaims(token),
        must_change_password: false,
      });
      return token;
    }
    try {
      const r = await api.refreshSession();
      applyRef.current(r.access_token, r.user);
      return r.access_token;
    } catch {
      signOutLocally();
      return null;
    }
  }, [signOutLocally]);
  renewRef.current = renew;

  // On-demand renewal for lib/api's 401 replay: a tab left in the background
  // has its timers throttled and comes back with an expired token.
  useEffect(() => {
    setTokenRenewer(() => renewRef.current());
    return () => setTokenRenewer(null);
  }, []);

  // Boot: ask the backend which mode is on, then restore a session.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let meta: Awaited<ReturnType<typeof api.meta>> | null = null;
      try {
        meta = await api.meta();
      } catch {
        // An unreachable backend leaves `meta` null, which the mode
        // resolution below reads as "none" — the dashboard still renders
        // instead of hanging on a blank screen.
      }
      if (cancelled) return;
      const resolved = (meta?.auth_mode ?? "none") as AuthMode;
      setMode(resolved);

      if (resolved === "none") {
        setUser(ANON_USER);
        setStatus("authenticated");
        return;
      }

      if (resolved === "oidc") {
        const config = meta?.oidc ?? null;
        if (!config?.configured) {
          // Deliberate: say what is missing rather than fail inside MSAL.
          setConfigError(i18n.t("auth.ssoNotConfigured"));
          setStatus("anonymous");
          return;
        }
        oidcRef.current = config;
        try {
          const { completeRedirect, acquireTokenSilently, currentAccount } =
            await import("./oidc");
          // The return leg from the IdP MUST be consumed before anything
          // else reads the account list.
          const token =
            (await completeRedirect(config)) ??
            (await acquireTokenSilently(config));
          if (cancelled) return;
          if (!token) {
            setStatus("anonymous");
            return;
          }
          const account = await currentAccount(config);
          if (cancelled) return;
          applySession(token, {
            email: account?.email ?? i18n.t("auth.unknownUser"),
            full_name: account?.name ?? account?.email ?? i18n.t("auth.unknownUser"),
            role: roleFromClaims(token),
            must_change_password: false,
          });
        } catch (err) {
          if (cancelled) return;
          setConfigError(err instanceof Error ? err.message : String(err));
          setStatus("anonymous");
        }
        return;
      }

      // local
      try {
        const r = await api.refreshSession();
        if (!cancelled) applySession(r.access_token, r.user);
      } catch {
        if (!cancelled) setStatus("anonymous");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  useEffect(() => clearTimer, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const r = await api.login(email, password);
      applySession(r.access_token, r.user);
      return r.user;
    },
    [applySession],
  );

  const signInWithProvider = useCallback(async () => {
    const config = oidcRef.current;
    if (!config) throw new Error(i18n.t("auth.ssoNotConfigured"));
    const { signIn } = await import("./oidc");
    await signIn(config);
  }, []);

  const logout = useCallback(async () => {
    clearTimer();
    const config = oidcRef.current;
    if (config) {
      setAccessToken(null);
      setUser(null);
      setStatus("anonymous");
      const { signOut } = await import("./oidc");
      // Ends the IdP session too — otherwise "sign out" silently signs the
      // same person straight back in on the next visit.
      await signOut(config);
      return;
    }
    try {
      await api.logout();
    } catch {
      /* best-effort — clear locally regardless */
    }
    signOutLocally();
  }, [signOutLocally]);

  const refreshUser = useCallback(async () => {
    // Under oidc the profile comes from the token, not from the users table.
    if (mode !== "local") return;
    try {
      const me = await api.me();
      setUser({
        email: me.email,
        full_name: me.full_name,
        role: me.role,
        must_change_password: me.must_change_password,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setStatus("anonymous");
      }
    }
  }, [mode]);

  const value = useMemo<AuthState>(
    () => ({
      enabled,
      mode,
      status,
      user,
      isSysadmin: user?.role === "sysadmin",
      login,
      signInWithProvider,
      logout,
      refreshUser,
      configError,
    }),
    [
      enabled,
      mode,
      status,
      user,
      login,
      signInWithProvider,
      logout,
      refreshUser,
      configError,
    ],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
